/**
 * Modal for creating a new deck
 */
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import Select, { type SingleValue, type StylesConfig } from 'react-select';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { useCreateDeckMutation } from '@/features/decks/hooks/api';
import { DECK_FORMAT_LABELS, DECK_STATUS_LABELS } from '@/lib/deckUtils';
import type { DeckFormat, DeckStatus } from '@/types/deck';
import { CreateDeckSchema } from '@/types/schemas';

export interface CreateDeckModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful deck creation with the new deck ID */
  onSuccess?: (deckId: string) => void;
}

interface FormatOption {
  value: DeckFormat;
  label: string;
}

const FORMAT_OPTIONS: FormatOption[] = Object.entries(DECK_FORMAT_LABELS).map(([value, label]) => ({
  value: value as DeckFormat,
  label,
}));

/** Format descriptions for user guidance */
const FORMAT_DESCRIPTIONS: Partial<Record<DeckFormat, string>> = {
  draft: '40-card minimum • Build from your draft pool • No copy limits',
  sealed: '40-card minimum • Build from your sealed pool • No copy limits',
  commander: '100-card singleton • Legendary commander required',
  brawl: '60-card singleton • Standard-legal commander',
  pauper: 'Commons only • 60-card minimum',
  standard: '60-card minimum • Recent sets only',
};

// ============================================================================
// React-Select Styles
// ============================================================================

const selectStyles: StylesConfig<FormatOption, false> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: 'var(--color-slate)',
    borderColor: state.isFocused ? 'var(--color-aether)' : 'var(--color-storm)',
    borderRadius: '0.5rem',
    minHeight: '2.625rem',
    boxShadow: state.isFocused ? '0 0 0 1px var(--color-aether)' : 'none',
    '&:hover': {
      borderColor: 'var(--color-storm)',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--color-slate)',
    border: '1px solid var(--color-storm)',
    borderRadius: '0.5rem',
    zIndex: 50,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: '200px',
    padding: '0.25rem',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'var(--color-storm)' : 'transparent',
    color: state.isSelected ? 'var(--color-pearl)' : 'var(--color-silver)',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: 'var(--color-storm)',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--color-pearl)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--color-pearl)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--color-ash)',
    fontSize: '0.875rem',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--color-ash)',
    padding: '0.5rem',
    '&:hover': {
      color: 'var(--color-silver)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--color-ash)',
    padding: '0.5rem',
    '&:hover': {
      color: 'var(--color-danger)',
    },
  }),
};

// ============================================================================
// Component
// ============================================================================

export default function CreateDeckModal({ isOpen, onClose, onSuccess }: CreateDeckModalProps): ReactElement {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [format, setFormat] = useState<FormatOption | null>(null);
  const [status, setStatus] = useState<DeckStatus>('brewing');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useCreateDeckMutation();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setFormat(null);
      setStatus('brewing');
      setErrors({});
      createMutation.reset();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormatChange = (option: SingleValue<FormatOption>) => {
    setFormat(option);
    if (errors.format) {
      setErrors((prev) => ({ ...prev, format: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const formData = {
      name: name.trim(),
      description: description.trim() || null,
      format: format?.value ?? null,
      status,
      enforce_legality: true,
    };

    // Validate with Zod
    const result = CreateDeckSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path[0];
        if (path && typeof path === 'string') {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    try {
      const newDeck = await createMutation.mutateAsync(result.data);
      onSuccess?.(newDeck.id);
      onClose();
    } catch (error) {
      console.error('Failed to create deck:', error);
      setErrors({ submit: 'Failed to create deck. Please try again.' });
    }
  };

  const isPending = createMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Deck" size="sm">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name input */}
        <div>
          <label htmlFor="deck-name" className="text-silver mb-1.5 block text-sm font-medium">
            Name <span className="text-danger">*</span>
          </label>
          <input
            id="deck-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
            }}
            placeholder="Enter deck name..."
            className="border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-full rounded-lg border px-4 py-2.5 text-sm"
            autoFocus
            maxLength={100}
            disabled={isPending}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'deck-name-error' : undefined}
          />
          {errors.name && (
            <p id="deck-name-error" className="text-danger mt-1 text-xs">
              {errors.name}
            </p>
          )}
        </div>

        {/* Description textarea */}
        <div>
          <label htmlFor="deck-description" className="text-silver mb-1.5 block text-sm font-medium">
            Description
          </label>
          <textarea
            id="deck-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description for your deck..."
            rows={3}
            className="border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-full resize-none rounded-lg border px-4 py-2.5 text-sm"
            maxLength={1000}
            disabled={isPending}
          />
          <p className="text-ash mt-1 text-xs">{description.length}/1000 characters</p>
        </div>

        {/* Format dropdown */}
        <div>
          <label htmlFor="deck-format" className="text-silver mb-1.5 block text-sm font-medium">
            Format
          </label>
          <Select<FormatOption, false>
            inputId="deck-format"
            options={FORMAT_OPTIONS}
            value={format}
            onChange={handleFormatChange}
            isClearable
            isSearchable
            placeholder="Select a format..."
            styles={selectStyles}
            isDisabled={isPending}
            aria-label="Deck format"
          />
          <p className="text-ash mt-1 text-xs">
            {format?.value && FORMAT_DESCRIPTIONS[format.value]
              ? FORMAT_DESCRIPTIONS[format.value]
              : 'Optional – Choose a format for deck validation'}
          </p>
        </div>

        {/* Status selector */}
        <div>
          <label className="text-silver mb-1.5 block text-sm font-medium">Status</label>
          <div className="flex flex-wrap gap-2">
            {(Object.entries(DECK_STATUS_LABELS) as [DeckStatus, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatus(value)}
                disabled={isPending}
                className={`rounded-full px-4 py-2 text-sm transition-colors ${
                  status === value ? 'bg-aether text-white' : 'bg-slate text-silver hover:bg-storm disabled:opacity-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit error */}
        {errors.submit && <div className="bg-danger/10 text-danger rounded-lg px-4 py-3 text-sm">{errors.submit}</div>}

        {/* Actions */}
        <div className="border-storm flex justify-end gap-3 border-t pt-4">
          <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
          <Button
            type="submit"
            text={isPending ? 'Creating...' : 'Create Deck'}
            color="create"
            onClick={() => {}}
            disabled={!name.trim() || isPending}
          />
        </div>
      </form>
    </Modal>
  );
}
